import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteShell } from "@/components/site-shell";
import { stories, getInstitution } from "@/data/sample";
import { BookmarkPlus, BookmarkCheck, Filter, Search, Calendar, Building2 } from "lucide-react";

export const Route = createFileRoute("/stories/")({
  head: () => ({
    meta: [
      { title: "Impact Stories — Manavta" },
      {
        name: "description",
        content: "Real stories of animals saved, sheltered and healed because of donor support.",
      },
    ],
  }),
  component: Stories,
});

const CATEGORIES = [
  "All",
  "Animal Rescue",
  "Medical Recovery",
  "Rehabilitation",
  "Shelter Expansion",
  "Bird Care",
];
const ANIMAL_TYPES = ["All", "Cow", "Bird", "Dog", "Horse", "Wildlife"];

function Stories() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [animalType, setAnimalType] = useState("All");
  const [saved, setSaved] = useState<string[]>([]);

  const filtered = stories.filter((s) => {
    const matchSearch =
      search === "" ||
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  return (
    <SiteShell>
      {/* ── Hero ── */}
      <section className="border-b border-border/60 bg-gradient-to-br from-[var(--cream)] to-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">
            Impact Stories
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl text-[var(--maroon-deep)]">
            Lives changed by your donations
          </h1>
          <p className="mt-3 max-w-2xl text-foreground/70">
            Every story here is from a verified institution, submitted with photo and expense proof
            — so you know your gift made a real difference.
          </p>

          {/* Search */}
          <div className="mt-6 flex max-w-lg items-center gap-2 rounded-full border border-border/60 bg-white px-4 py-2.5 shadow-xs">
            <Search className="h-4 w-4 text-foreground/40 shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stories…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground/45"
            />
          </div>

          {/* Category pills */}
          <div className="mt-4 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                  category === c
                    ? "border-[var(--maroon)] bg-[var(--maroon)] text-white"
                    : "border-border bg-white text-foreground/70 hover:border-[var(--maroon)]/40 hover:text-[var(--maroon)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <div className="border-b border-border/40 bg-white">
        <div className="mx-auto flex max-w-7xl divide-x divide-border/50 px-4 sm:px-6">
          {[
            { label: "Stories published", value: "284" },
            { label: "Animals featured", value: "1,200+" },
            { label: "Institutions contributing", value: "48" },
            { label: "Donor reach", value: "32K" },
          ].map((s) => (
            <div key={s.label} className="flex-1 px-4 py-4 text-center first:pl-0 last:pr-0">
              <p className="font-display text-xl font-bold text-[var(--maroon-deep)]">{s.value}</p>
              <p className="text-[11px] text-foreground/60">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Stories Grid ── */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {filtered.length === 0 ? (
          <div className="py-20 text-center text-foreground/50">No stories match your search.</div>
        ) : (
          <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s) => {
              const inst = getInstitution(s.institutionId);
              const isSaved = saved.includes(s.id);
              return (
                <article
                  key={s.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={s.image}
                      alt=""
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <button
                      onClick={() =>
                        setSaved((prev) =>
                          isSaved ? prev.filter((id) => id !== s.id) : [...prev, s.id],
                        )
                      }
                      className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 backdrop-blur shadow transition hover:bg-white"
                    >
                      {isSaved ? (
                        <BookmarkCheck className="h-4 w-4 text-[var(--maroon)]" />
                      ) : (
                        <BookmarkPlus className="h-4 w-4 text-foreground/60" />
                      )}
                    </button>
                    <span className="absolute left-3 top-3 rounded-full bg-[var(--saffron)]/90 px-2.5 py-1 text-[10px] font-bold text-[var(--maroon-deep)] backdrop-blur">
                      {CATEGORIES[Math.floor(Math.random() * (CATEGORIES.length - 1)) + 1]}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center gap-2 text-xs text-foreground/60">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(s.date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                      {inst && (
                        <>
                          <span>·</span>
                          <Building2 className="h-3.5 w-3.5" />
                          {inst.name}
                        </>
                      )}
                    </div>
                    <h3 className="mt-2 font-display text-lg font-semibold leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm text-foreground/70 line-clamp-3">
                      {s.excerpt}
                    </p>
                    <Link
                      to="/stories/$id"
                      params={{ id: s.id }}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--maroon)] hover:gap-2.5 transition-all"
                    >
                      Read full story <span aria-hidden>→</span>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Load more */}
        <div className="mt-12 text-center">
          <button className="rounded-full border border-[var(--maroon)]/30 bg-[var(--cream-2)] px-8 py-3 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--maroon)] hover:text-white transition">
            Load more stories
          </button>
        </div>
      </section>

      {/* ── Submit a story CTA ── */}
      <section className="border-t border-border/40 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 text-center">
          <p className="font-display text-xl font-bold text-[var(--maroon-deep)]">
            Are you an institution with a story to share?
          </p>
          <p className="mt-2 text-sm text-foreground/70">
            Submit your animal rescue or rehabilitation story — our team will review and publish it
            within 48 hours.
          </p>
          <Link
            to="/institution/impact"
            className="mt-5 inline-block rounded-full bg-[var(--maroon)] px-6 py-2.5 text-sm font-bold text-white hover:bg-[var(--maroon-deep)] transition"
          >
            Submit a story →
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}
